/**
 * Fabrique — état de la flotte de runners de build.
 *
 * Lecture seule, et disponible sur les deux environnements : la flotte est un
 * singleton de production, mais dev et prod partagent le compte AWS, donc le
 * control plane de dev observe la même flotte.
 *
 * Le coût vit sur une route à part parce que Cost Explorer facture chaque
 * requête et ne rafraîchit ses données que quelques fois par jour : la page
 * scrute les instances, elle ne scrute pas la facture.
 */

import { apiCall } from "@/api/client";

export interface FleetInstance {
  instanceId: string;
  instanceType: string | null;
  state: string | null;
  spot: boolean;
  availabilityZone: string | null;
  provider: string | null;
  /** Nom court du pool, dérivé du provider (« PoolNano »). */
  pool: string | null;
  /** Étiquettes réellement demandées par GitHub, résolues à l'exécution. */
  labels: string | null;
  launchedAt: number | null;
  ageSeconds: number | null;
}

export interface FleetExecution {
  name: string | null;
  status: string | null;
  startedAt: number | null;
}

export interface FleetImage {
  name: string | null;
  status: string | null;
  lastRun: string | null;
  nextRun: string | null;
  schedule: string | null;
}

/**
 * État d'un pool entre le désiré (`runner_pools.json`) et le déployé.
 *
 * - `in_sync`       : identiques
 * - `drifted`       : déclaré et déployé, mais différents — un `cdk deploy` de
 *                     runner-fleet reste à faire
 * - `desired_only`  : déclaré, jamais déployé
 * - `live_only`     : encore déployé alors qu'il n'est plus déclaré ; il
 *                     continue de coûter et d'accepter des jobs
 */
export type PoolState = "in_sync" | "drifted" | "desired_only" | "live_only";

export interface DesiredPool {
  label: string;
  instanceType: string;
  storageGib: number;
  spot: boolean;
  purpose: string;
}

export interface LivePool {
  instanceType: string;
  storageGib: number | null;
  spot: boolean;
  volumeType: string | null;
  throughput: number | null;
  iops: number | null;
}

export interface PoolDrift {
  label: string;
  state: PoolState;
  /** Phrases prêtes à afficher, une par écart constaté. */
  differences: string[];
  desired: DesiredPool | null;
  live: LivePool | null;
}

export interface FleetSnapshot {
  stack: string;
  instances: FleetInstance[];
  executions: {
    running: FleetExecution[];
    recentFailures: FleetExecution[];
  };
  images: FleetImage[];
  pools: PoolDrift[];
  /**
   * Sections que l'appel AWS n'a pas pu lire à ce passage.
   *
   * Une section en échec revient vide, et une liste vide se lit « il n'y a
   * rien » — ce qui est une affirmation, alors que la seule chose vraie est
   * « je n'ai pas pu regarder ». Ce champ permet de le dire. Même distinction
   * que « absent » contre « non observé » du côté des identifiants.
   */
  unavailable: string[];
}

export interface FleetCost {
  monthToDateUsd: number;
  byService: Record<string, number>;
  measuredAt: number | null;
  periodStart: string | null;
  /** Vrai quand la valeur vient de l'instantané du jour, pas d'un appel frais. */
  cached: boolean;
}

export function getFleet(token: string) {
  return apiCall<FleetSnapshot>("/factory/fleet", { token });
}

export function getFleetCost(token: string) {
  return apiCall<FleetCost>("/factory/fleet/cost", { token });
}

/* -------------------------------------------------------------------------
 * Actions — production seulement
 *
 * Les routes ci-dessous n'existent PAS sur l'API de dev : la garde est le 404
 * d'API Gateway, pas le drapeau `isProduction` qui se contente de ne pas
 * proposer un bouton voué à échouer.
 *
 * Deux d'entre elles sont des lectures. Elles vivent ici parce qu'elles
 * interrogent GitHub — les runners enregistrés, les propositions ouvertes — ce
 * qui demande le jeton d'amorçage : le donner au point d'entrée visible depuis
 * dev reviendrait à le donner à dev. Même raison que le rafraîchissement des
 * identifiants.
 * ---------------------------------------------------------------------- */

export interface RegisteredRunner {
  id: number;
  /**
   * Dépôt d'enregistrement.
   *
   * Les runners s'enregistrent au niveau du DÉPÔT, pas de l'organisation : un
   * identifiant n'est donc unique que là, et il faut interroger chaque dépôt
   * consommateur. Interroger l'organisation rend une liste vide — pour
   * toujours — ce qui se lit « aucun fantôme ».
   */
  repo: string;
  name: string;
  status: string | null;
  busy: boolean;
  labels: string[];
  /**
   * Enregistré chez GitHub alors qu'aucune instance ne porte plus son nom.
   *
   * Hors ligne ne suffit pas : une machine peut être brièvement injoignable.
   * C'est le croisement avec les instances réellement en vol qui transforme
   * le soupçon en constat — et un fantôme continue de se voir attribuer des
   * jobs, qui attendent alors jusqu'au délai de 24 h.
   */
  ghost: boolean;
}

/** Une modification des pools en attente de revue. */
export interface PoolProposal {
  number: number;
  title: string | null;
  url: string | null;
  branch: string;
  createdAt: string | null;
  pools: DesiredPool[];
  /** Renseigné quand la branche existe mais que son fichier est illisible. */
  unreadable: string | null;
}

export function listRegisteredRunners(token: string) {
  return apiCall<{
    runners: RegisteredRunner[];
    org: string;
    /** Dépôts que le jeton n'a pas pu lire — nommés, jamais comptés pour zéro. */
    unavailable: { repo: string; reason: string }[];
  }>("/factory/fleet/runners", { token });
}

export function listPoolProposals(token: string) {
  return apiCall<{
    proposals: PoolProposal[];
    /**
     * Les pools tels qu'ils sont sur `main`.
     *
     * C'est la base de l'éditeur, et non l'instantané que porte la Lambda de
     * lecture : le fichier proposé écrase `main`, donc partir d'un état figé
     * au dernier déploiement de l'API annulerait en silence toute proposition
     * fusionnée depuis. `null` quand GitHub n'a pas pu être lu.
     */
    repositoryPools: DesiredPool[] | null;
    /**
     * La vue de dérive recalculée depuis `main`.
     *
     * Celle de `/factory/fleet` part de l'instantané figé au dernier
     * déploiement de l'API : dès qu'une proposition est fusionnée, elle
     * annonce une dérive là où le dépôt et la flotte sont d'accord. Quand ce
     * champ est présent, il remplace l'autre.
     */
    repositoryDrift: PoolDrift[] | null;
  }>("/factory/fleet/proposals", { token });
}

export function terminateInstance(token: string, instanceId: string) {
  return apiCall<{ instanceId: string; previousState: string | null }>(
    `/factory/fleet/instances/${encodeURIComponent(instanceId)}/terminate`,
    { method: "POST", body: {}, token },
  );
}

/**
 * Stoppe une exécution, après avoir terminé les instances indiquées.
 *
 * L'ordre n'est pas un détail : l'exécution détient le task token et le
 * battement de cœur de l'user-data. La stopper d'abord laisserait l'instance
 * en vol, facturée jusqu'à ce que le ramasseur la trouve dix minutes plus
 * tard. Le serveur fait les deux dans cet ordre, et refuse de stopper si une
 * terminaison a échoué.
 */
export function stopExecution(
  token: string,
  executionName: string,
  terminateInstances: string[] = [],
) {
  return apiCall<{ execution: string; terminated: { id: string; ok: boolean }[] }>(
    `/factory/fleet/executions/${encodeURIComponent(executionName)}/stop`,
    { method: "POST", body: { terminate_instances: terminateInstances }, token },
  );
}

export function rebuildImage(token: string, pipelineName: string) {
  return apiCall<{ pipeline: string; buildVersionArn: string | null }>(
    `/factory/fleet/images/${encodeURIComponent(pipelineName)}/rebuild`,
    { method: "POST", body: {}, token },
  );
}

export function deregisterRunner(
  token: string,
  repo: string,
  runnerId: number,
) {
  return apiCall<{ runnerId: number; name: string | null }>(
    `/factory/fleet/runners/${encodeURIComponent(repo)}/${runnerId}/deregister`,
    { method: "POST", body: {}, token },
  );
}

export interface ProposalResult {
  dispatched: boolean;
  changes: string[];
  added: string[];
  removed: string[];
  modified: string[];
}

/**
 * Propose une nouvelle liste de pools.
 *
 * Rien n'est appliqué : une pull request est ouverte, à relire et à fusionner,
 * puis la flotte doit être déployée à la main — ce dépôt n'a aucun
 * déploiement automatique.
 */
export function proposePools(token: string, pools: DesiredPool[]) {
  return apiCall<ProposalResult>("/factory/fleet/pools/propose", {
    method: "POST",
    body: {
      pools: pools.map((pool) => ({
        label: pool.label,
        instance_type: pool.instanceType,
        storage_gib: pool.storageGib,
        spot: pool.spot,
        purpose: pool.purpose,
      })),
    },
    token,
  });
}
